import type { StaffAccountDto } from "@shoppy/shared";
import type { Searchable } from "./search";

/**
 * The staff directory, as words — `GET /v1/admin/accounts`.
 *
 * The api answers a colleague list: id, display name, roles, last seen, and
 * **no phone and no email, deliberately**. This module is the half that turns
 * it into something a register and a picker can draw, and it is separate from
 * both so it is testable without a DOM.
 *
 * ── Why a name has a fallback at all ────────────────────────────────────────
 * `displayName` is nullable — nobody has set one — and the DTO's own comment
 * says what to fall back to: the id's first octet, **never the phone**. The
 * phone is not in the response, so the rule is enforced by the api rather than
 * by remembering it here; what this module must not do is draw a bare `null`,
 * which on a register reads as a row that failed to load rather than as a
 * colleague who has not filled in their name.
 */

/** The eight hex characters before the first hyphen of a uuid. Enough to tell two colleagues apart in a picker. */
export function shortId(id: string): string {
  const hyphen = id.indexOf("-");
  return hyphen === -1 ? id.slice(0, 8) : id.slice(0, hyphen);
}

/**
 * What to draw for one colleague.
 *
 * `unnamed` is the caller's word for «this account has no display name», passed
 * in rather than written here because every string this console draws is a
 * Lingui message and `lingui extract` cannot see through a module that is not a
 * component (`src/i18n/vocabulary.ts` carries the same argument for statuses).
 */
export function staffLabel(account: StaffAccountDto, unnamed: (short: string) => string): string {
  const name = account.displayName?.trim();
  return name === undefined || name === "" ? unnamed(shortId(account.id)) : name;
}

/**
 * The name for an id the directory knows, or `null`.
 *
 * `null` is a real answer and not a failure: an assignee who has since lost
 * their staff role, or an account above the directory's cap, is an id this
 * console genuinely cannot name. The caller draws «somebody else» for it rather
 * than a uuid — see `assigneeWord`.
 */
export function nameOf(
  directory: readonly StaffAccountDto[] | null,
  id: string,
  unnamed: (short: string) => string,
): string | null {
  if (directory === null) return null;
  const found = directory.find((account) => account.id === id);
  return found ? staffLabel(found, unnamed) : null;
}

/**
 * The «Assigned to» cell, in one place because three screens would otherwise
 * each invent their own three words.
 *
 * Four cases, and the fourth is why this is a function rather than a ternary:
 *
 *  - nobody has it;
 *  - **you** have it — the fact an agent working a desk scans for, so it stays
 *    a word of its own rather than becoming their own name in a list of names;
 *  - a colleague the directory can name;
 *  - a colleague it cannot. That is not the same as «nobody»: the ticket IS
 *    assigned, and saying otherwise would send a second agent to answer it.
 */
export interface AssigneeWords {
  readonly nobody: string;
  readonly you: string;
  readonly somebodyElse: string;
  readonly unnamed: (short: string) => string;
}

export function assigneeWord(
  assigneeAccountId: string | null,
  myAccountId: string | null,
  directory: readonly StaffAccountDto[] | null,
  words: AssigneeWords,
): string {
  if (assigneeAccountId === null) return words.nobody;
  if (myAccountId !== null && assigneeAccountId === myAccountId) return words.you;
  return nameOf(directory, assigneeAccountId, words.unnamed) ?? words.somebodyElse;
}

/**
 * The picker's rows — `ComboboxOption`, plus the haystack §11.7's matcher reads.
 *
 * The haystack carries the name **and the short id**, because the one thing an
 * operator can copy out of an audit row or a URL is the id, and a picker that
 * could not be searched by it would send them back to the register to read a
 * name they already had the id for.
 *
 * The order is the api's: named accounts first, then alphabetically. Re-sorting
 * here would be a second opinion about a list that already has one.
 */
export interface StaffOption extends Searchable {
  readonly value: string;
  readonly label: string;
}

export function staffOptions(
  directory: readonly StaffAccountDto[],
  unnamed: (short: string) => string,
): StaffOption[] {
  return directory.map((account) => {
    const label = staffLabel(account, unnamed);
    return { value: account.id, label, haystack: [label, shortId(account.id)] };
  });
}
